import React from 'react';
import { Box } from '@mui/material';

interface CreditScoreGaugeProps {
  score: number;
  min?: number;
  max?: number;
}

const RANGES = [
  { label: 'Poor', min: 300, max: 450, color: '#e53935' },
  { label: 'Average', min: 450, max: 600, color: '#fb8c00' },
  { label: 'Good', min: 600, max: 750, color: '#c0ca33' },
  { label: 'Excellent', min: 750, max: 900, color: '#43a047' },
];

const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
  const angleInRadians = ((angleInDegrees - 180) * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {
  const start = polarToCartesian(x, y, radius, startAngle);
  const end = polarToCartesian(x, y, radius, endAngle);
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 0 1 ${end.x} ${end.y}`;
};

const CreditScoreGauge: React.FC<CreditScoreGaugeProps> = ({ score = 0, min = 300, max = 900 }) => {
  const size = 250;
  const center = size / 2;
  const radius = center - 45;
  const strokeWidth = 30;
  const totalAngle = 180;
  
  const scoreAngle = Math.max(0, Math.min(totalAngle, (totalAngle * (score - min)) / (max - min)));

  const needleColor = '#333';
  const currentRange = RANGES.find(r => score >= r.min && score <= r.max) || RANGES[0];

  return (
    <Box sx={{ position: 'relative', width: size, height: size / 2 + 50, mx: 'auto' }}>
      <svg width={size} height={size / 2 + 50} viewBox={`0 0 ${size} ${size / 2 + 50}`} style={{ overflow: 'visible' }}>
        <defs>
            {RANGES.map((range, index) => {
                const startAngle = (totalAngle * (range.min - min)) / (max - min);
                const endAngle = (totalAngle * (range.max - min)) / (max - min);
                return (
                    <path
                        key={`arc-path-${index}`}
                        id={`arc-path-${index}`}
                        d={describeArc(center, center, radius, startAngle, endAngle)}
                        fill="none"
                        stroke="transparent"
                    />
                );
            })}
        </defs>
        
        {/* Gauge Arcs */}
        {RANGES.map((range) => {
          const startAngle = (totalAngle * (range.min - min)) / (max - min);
          const endAngle = (totalAngle * (range.max - min)) / (max - min);
          return (
            <path
              key={range.label}
              d={describeArc(center, center, radius, startAngle, endAngle)}
              fill="none"
              stroke={range.color}
              strokeWidth={strokeWidth}
            />
          );
        })}
        
        {/* Text Labels on Arc */}
        {RANGES.map((range, index) => (
             <text key={`text-${index}`} dy={-5} style={{ fontSize: '10px', fill: '#fff', fontWeight: 'bold', textAnchor: 'middle' }}>
                <textPath href={`#arc-path-${index}`} startOffset="50%">
                    {range.label.toUpperCase()}
                </textPath>
            </text>
        ))}
        
        {/* Center Text */}
        <text x={center} y={center - 15} textAnchor="middle" fontSize="2.5rem" fontWeight="bold" fill={currentRange.color}>
          {Math.round(score)}
        </text>
        <text x={center} y={center + 5} textAnchor="middle" fontSize="0.8rem" fill="#666">
          {currentRange.label}
        </text>

        {/* Needle */}
        <g transform={`rotate(${scoreAngle - 90}, ${center}, ${center})`}>
          <path d={`M ${center} ${center - radius + 5} L ${center - 6} ${center + 10} L ${center + 6} ${center + 10} Z`} fill={needleColor} />
          <circle cx={center} cy={center} r={8} fill={needleColor} />
          <circle cx={center} cy={center} r={4} fill="#fff" />
        </g>
      </svg>
    </Box>
  );
};

export default CreditScoreGauge; 